import type { Lang } from '../../i18n'
import type { ArtId } from './EggArt'

/**
 * Language easter eggs. Each language has a launch call, a confetti mix fired from every rocket
 * launch and three floating collectibles with a secret line; collecting all three unlocks a badge.
 * Kept always-on for now; a holiday calendar will switch themed packs on by date later.
 */

export type EggItem = { id: ArtId; label: string; secret: string }

export type EggPack = {
  launch: string
  confetti: ArtId[]
  items: [EggItem, EggItem, EggItem]
  hint: string
  found: string
  complete: { title: string; text: string; cta: string }
  hide: string
  show: string
}

export const eggPacks: Record<Lang, EggPack> = {
  ru: {
    launch: 'Поехали!',
    confetti: ['pennant', 'tube', 'sputnik', 'star'],
    items: [
      { id: 'tube', label: 'Тюбик из космического меню', secret: 'Меню космонавта: борщ в тюбике. Питательно, герметично, без крошек в невесомости!' },
      { id: 'sputnik', label: 'Первый спутник', secret: '«Бип-бип!» — так в 1957 году звучал первый искусственный спутник Земли. Мы тоже начинаем с простого сигнала — вашей задачи.' },
      { id: 'dog', label: 'Космическая собака', secret: 'Белка и Стрелка слетали в космос и вернулись домой. Наши проекты тоже всегда возвращаются с результатом.' },
    ],
    hint: 'В космосе что-то летает — поймайте все 3 находки',
    found: 'Найдено',
    complete: { title: 'Коллекция космонавта собрана!', text: 'Вы нашли все секреты русской версии. Такое внимание к деталям мы ценим — давайте сделаем проект вместе.', cta: 'Полететь к планете «Ваш проект»' },
    hide: 'Убрать находки',
    show: 'Вернуть находки',
  },
  en: {
    launch: 'Houston, we have no problems',
    confetti: ['footprint', 'helmet', 'star', 'telescope'],
    items: [
      { id: 'footprint', label: 'A footprint on the Moon', secret: '“One small step…” Footprints on the Moon can last for millions of years. Good architecture should too.' },
      { id: 'helmet', label: 'Astronaut helmet', secret: 'Gold-coated visors protect astronauts from sunlight. We protect your data with the same care.' },
      { id: 'telescope', label: 'Space telescope', secret: 'Space telescopes see billions of years back in time. We look at your data to see what comes next.' },
    ],
    hint: 'Something is drifting in space — catch all 3 finds',
    found: 'Found',
    complete: { title: 'Mission log complete!', text: 'You found every secret of the English edition. We love that attention to detail — let’s build something together.', cta: 'Fly to “Your project”' },
    hide: 'Hide finds',
    show: 'Show finds',
  },
  de: {
    launch: 'Los geht’s!',
    confetti: ['pretzel', 'retro', 'orbits', 'star'],
    items: [
      { id: 'pretzel', label: 'Brezel im Orbit', secret: 'Auch im All gilt: erst die Brezel, dann die Mission. Guten Appetit, Crew!' },
      { id: 'retro', label: 'Retro-Rakete', secret: 'Hermann Oberth gilt als einer der Väter der Raumfahrt. Solide Ingenieurskunst hebt eben ab.' },
      { id: 'orbits', label: 'Planetenbahnen', secret: 'Johannes Kepler hat die Bahnen der Planeten berechnet. Wir berechnen die Bahn Ihres Projekts.' },
    ],
    hint: 'Im All schwebt etwas — sammeln Sie alle 3 Fundstücke',
    found: 'Gefunden',
    complete: { title: 'Sammlung komplett!', text: 'Sie haben alle Geheimnisse der deutschen Version gefunden. Diese Gründlichkeit schätzen wir — bauen wir gemeinsam etwas.', cta: 'Zum Planeten „Ihr Projekt“' },
    hide: 'Fundstücke ausblenden',
    show: 'Fundstücke zeigen',
  },
  es: {
    launch: '¡Despegamos!',
    confetti: ['star', 'churro', 'windmill', 'star'],
    items: [
      { id: 'star', label: 'Estrella fugaz', secret: '¡Una estrella fugaz! Pide un deseo… nosotros lo convertimos en software.' },
      { id: 'windmill', label: 'Molino de viento', secret: 'Don Quijote veía gigantes en los molinos. Nosotros vemos oportunidades en los problemas complejos.' },
      { id: 'churro', label: 'Churro espacial', secret: 'Un churro en órbita: la mejor gasolina para programar. ¿Con chocolate?' },
    ],
    hint: 'Algo flota en el espacio — atrapa los 3 hallazgos',
    found: 'Encontrado',
    complete: { title: '¡Colección completa!', text: 'Has encontrado todos los secretos de la versión en español. Nos encanta esa atención al detalle: construyamos algo juntos.', cta: 'Volar a «Tu proyecto»' },
    hide: 'Ocultar hallazgos',
    show: 'Mostrar hallazgos',
  },
  zh: {
    launch: '出发！',
    confetti: ['redlantern', 'mooncake', 'rabbit', 'star'],
    items: [
      { id: 'rabbit', label: '玉兔', secret: '玉兔不仅住在月亮上，也是中国月球车的名字。它也来帮忙啦，祝您的项目顺利升空！' },
      { id: 'redlantern', label: '红灯笼', secret: '红灯笼寓意吉祥如意。愿您的每一个项目都红红火火！' },
      { id: 'mooncake', label: '月饼', secret: '中秋赏月吃月饼，象征团圆。好的团队，也是一个整体。' },
    ],
    hint: '太空中漂浮着一些东西——找到全部 3 个彩蛋',
    found: '已找到',
    complete: { title: '彩蛋收集完成！', text: '您找到了中文版的全部秘密。我们欣赏这样的细心——一起打造您的项目吧。', cta: '飞往“您的项目”' },
    hide: '隐藏彩蛋',
    show: '显示彩蛋',
  },
  th: {
    launch: 'ไปกันเลย!',
    confetti: ['khomloi', 'bangfai', 'lotus', 'khomloi'],
    items: [
      { id: 'khomloi', label: 'โคมลอย', secret: 'ปล่อยโคมลอยพร้อมคำอธิษฐาน ขอให้ทุกความฝันของคุณลอยสูงขึ้นไปบนฟ้า' },
      { id: 'bangfai', label: 'บั้งไฟ', secret: 'ประเพณีบุญบั้งไฟ — จรวดแห่งความหวังของภาคอีสาน เราก็ชอบส่งจรวดเหมือนกัน!' },
      { id: 'lotus', label: 'ดอกบัว', secret: 'ดอกบัวบานพ้นน้ำอย่างงดงาม เหมือนโปรเจกต์ที่ผ่านการทดสอบมาอย่างดี' },
    ],
    hint: 'มีบางอย่างลอยอยู่ในอวกาศ — จับให้ครบทั้ง 3 ชิ้น',
    found: 'พบแล้ว',
    complete: { title: 'สะสมครบแล้ว!', text: 'คุณค้นพบความลับทั้งหมดของเวอร์ชันภาษาไทย เราชื่นชมความใส่ใจในรายละเอียดนี้ — มาสร้างโปรเจกต์ด้วยกันนะ', cta: 'บินไปที่ “โปรเจกต์ของคุณ”' },
    hide: 'ซ่อนของที่ค้นพบ',
    show: 'แสดงของที่ค้นพบ',
  },
}
